import { AI_FALLBACK_ORIGIN, AI_REPORT_PATH } from './agent-keys';
import type { Shell } from './agent-command';

const CURRENT_TASK_TOOLS = ['course_status', 'run_command', 'list_files', 'read_file', 'write_file'];
const ROUTING_ENV = ['OPENAI_API_KEY', 'CODEX_API_KEY', 'OPENAI_BASE_URL', 'OPENAI_API_BASE', 'OPENAI_ORG_ID', 'OPENAI_PROJECT_ID', 'CODEX_THREAD_ID'];
const REPORT_ENV = ['EOS_CODEX_AGENT_TOKEN'];

// TOML literal strings also survive Windows PowerShell 5's native argument parser,
// which strips embedded double quotes. These values never contain a newline.
const toml = (value: string) => `'${value}'`;

/** Capture one owner-supplied brief, then start one isolated native Codex run.
 * exec is intentional: the native interactive command cannot ignore user config.
 * The saved ChatGPT login stays with the official client; no credentials are copied.
 * The owner checks the result and starts a fresh command for a replay.
 */
export function codexCommand(origin: string, key: string, shell: Shell, smallerModel = false, model?: 'gpt-6.1-sol' | 'gpt-6-luna'): string {
  if (!/^[A-Za-z0-9_-]+$/.test(key)) throw new Error('Invalid agent key');
  const candidate = origin.trim();
  const base = /^https?:\/\/[^\s"']+$/.test(candidate) ? candidate.replace(/\/+$/, '') : AI_FALLBACK_ORIGIN;
  const quote = (value: string) => `'${value.replace(/'/g, shell === 'posix' ? `'\\''` : `''`)}'`;
  const selectedModel = model ?? (smallerModel ? 'gpt-6-luna' : 'gpt-6.1-sol');
  const developer = 'Work only on the task the owner gives you in the remote EngineerOS workspace. Use only the configured EngineerOS MCP task tools. Do not inspect host files, run local commands, use the web, spawn agents, open or reset levels, grade work, or advance to another task. Ask the owner if needed. When ready for Check, summarize your work and stop. The owner controls grading and the next run.';
  const config = [
    `forced_login_method=${toml('chatgpt')}`, `approval_policy=${toml('never')}`,
    `model_reasoning_effort=${toml('medium')}`, 'project_doc_max_bytes=0',
    'features.shell_tool=false', 'features.apps=false', 'features.multi_agent=false',
    'features.plugins=false', 'features.skip_host_skill_discovery=true',
    'features.hooks=false', 'features.memories=false',
    `history.persistence=${toml('none')}`, `web_search=${toml('disabled')}`,
    `developer_instructions=${toml(developer)}`,
    `mcp_servers.engineeros={url=${toml(`${base}/api/mcp`)},bearer_token_env_var=${toml('EOS_CODEX_AGENT_TOKEN')},required=true,default_tools_approval_mode=${toml('approve')},tool_timeout_sec=180,enabled_tools=[${CURRENT_TASK_TOOLS.map(toml).join(',')}]}`,
    'otel.log_user_prompt=false', 'otel.log_agent_responses=false',
    // Native Codex does not expand environment placeholders in exporter headers.
    // As with the existing Claude launch command, this owner-only command contains the key.
    `otel.exporter={otlp-http={endpoint=${toml(`${base}${AI_REPORT_PATH}/v1/logs`)},protocol=${toml('json')},headers={Authorization=${toml(`Bearer ${key}`)}}}}`,
  ];
  const argv = ['exec', '--ignore-user-config', '--ignore-rules', '--ephemeral', '--skip-git-repo-check',
    '--sandbox', 'read-only', '--color', 'never', '--model', selectedModel,
    ...config.flatMap((setting) => ['-c', setting]), '-'];
  const launch = `codex ${argv.map(quote).join(' ')}`;
  const prompt = 'Paste your full brief on one line, including any replay instruction, then press Enter. The agent will stop for your Check.';
  if (shell === 'posix') {
    return [
      '(', `printf '%s\\n' ${quote(prompt)}`,
      'IFS= read -r eos_codex_brief || exit 1', '[ -n "$eos_codex_brief" ] || exit 1',
      'eos_codex_work=$(mktemp -d "${TMPDIR:-/tmp}/engineeros-codex.XXXXXX") || exit 1',
      'cd "$eos_codex_work" || exit 1',
      `unset ${ROUTING_ENV.join(' ')}`,
      `export EOS_CODEX_AGENT_TOKEN=${quote(key)}`,
      `printf '%s\\n' "$eos_codex_brief" | ${launch}`, ')',
    ].join('\n');
  }
  return [
    '& {', "$ErrorActionPreference='Stop'", `$eosCodexBrief=Read-Host ${quote(prompt)}`,
    'if ([string]::IsNullOrWhiteSpace($eosCodexBrief)) { throw \'A task brief is required\' }',
    '$eosCodexWork=Join-Path ([IO.Path]::GetTempPath()) (\'engineeros-codex-\'+[Guid]::NewGuid().ToString(\'N\'))',
    '[void](New-Item -ItemType Directory -Path $eosCodexWork)',
    `$eosCodexNames=@(${[...ROUTING_ENV, ...REPORT_ENV].map(quote).join(',')})`,
    '$eosCodexSaved=@{}; foreach ($eosCodexName in $eosCodexNames) { $eosCodexSaved[$eosCodexName]=[Environment]::GetEnvironmentVariable($eosCodexName,\'Process\') }',
    '$eosCodexEncoding=$OutputEncoding', '$eosCodexConsoleEncoding=[Console]::OutputEncoding', 'Push-Location -LiteralPath $eosCodexWork', 'try {',
    `foreach ($eosCodexName in @(${ROUTING_ENV.map(quote).join(',')})) { [Environment]::SetEnvironmentVariable($eosCodexName,$null,'Process') }`,
    `$env:EOS_CODEX_AGENT_TOKEN=${quote(key)}`,
    '$OutputEncoding=[Text.UTF8Encoding]::new($false)', '[Console]::OutputEncoding=[Text.UTF8Encoding]::new($false)', `$eosCodexBrief | ${launch}`,
    '} finally {', 'Pop-Location', '$OutputEncoding=$eosCodexEncoding', '[Console]::OutputEncoding=$eosCodexConsoleEncoding',
    'foreach ($eosCodexName in $eosCodexNames) { [Environment]::SetEnvironmentVariable($eosCodexName,$eosCodexSaved[$eosCodexName],\'Process\') }',
    '}', '}',
  ].join('\n');
}
